import React from 'react';
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';

/**
 * Componente "Lista" do checkout (modelo Hotmart): título opcional + itens
 * com marcador (check, número, ponto ou seta), alinhamento, cor de fundo,
 * cor e tamanho dos itens.
 */

export type ListType = 'check' | 'numero' | 'ponto' | 'seta';
export type ListAlign = 'left' | 'center' | 'right';

export interface ListConfig {
    listType?: ListType;
    align?: ListAlign;
    backgroundColor?: string; // '' = transparente
    title?: string;
    itemColor?: string;
    iconColor?: string; // '' = padrão (verde no check, cor do item nos demais)
    itemSize?: number; // px
    items?: string[];
}

export const LIST_DEFAULTS = {
    listType: 'check' as ListType,
    align: 'left' as ListAlign,
    backgroundColor: '',
    title: '',
    itemColor: '#555555',
    itemSize: 18,
};

export const CHECK_COLOR = '#16A34A';

/** Cor padrão do ícone quando o produtor não escolheu uma. */
export const defaultIconColor = (type: ListType, itemColor: string) => (type === 'check' ? CHECK_COLOR : itemColor);

const Marker: React.FC<{ type: ListType; index: number; size: number; color: string }> = ({ type, index, size, color }) => {
    const box = Math.round(size * 1.15);
    if (type === 'numero') {
        return (
            <span className="shrink-0 font-semibold tabular-nums" style={{ color, minWidth: box, lineHeight: 1.35 }}>
                {index + 1}.
            </span>
        );
    }
    if (type === 'ponto') {
        return (
            <span className="shrink-0 flex items-center justify-center" style={{ width: box, height: size * 1.35 }}>
                <span className="rounded-full" style={{ width: size * 0.35, height: size * 0.35, backgroundColor: color }} />
            </span>
        );
    }
    if (type === 'seta') {
        return (
            <svg className="shrink-0" width={box} height={size * 1.35} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12h13m-5-6 6 6-6 6" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
    }
    // check
    return (
        <svg className="shrink-0" width={box} height={size * 1.35} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" fill={color} />
            <path d="m7.5 12.3 3 3 6-6.3" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

export const ListBlock: React.FC<{ config: ListConfig; placeholderWhenEmpty?: boolean }> = ({ config, placeholderWhenEmpty = false }) => {
    const listType = config.listType || LIST_DEFAULTS.listType;
    const align = config.align || LIST_DEFAULTS.align;
    const size = Number(config.itemSize) || LIST_DEFAULTS.itemSize;
    const color = config.itemColor || LIST_DEFAULTS.itemColor;
    const iconColor = config.iconColor || defaultIconColor(listType, color);
    const bg = config.backgroundColor || '';
    const title = config.title?.trim() || '';
    const items = (config.items || []).filter((i) => i && i.trim());

    // Centro/direita movem a lista inteira; os itens continuam alinhados entre si
    const listPos = align === 'center' ? 'mx-auto w-fit max-w-full' : align === 'right' ? 'ml-auto w-fit max-w-full' : '';
    const textAlign = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';

    if (!title && items.length === 0) {
        if (!placeholderWhenEmpty) return null;
        return (
            <div className="w-full rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-400">
                Lista vazia — adicione itens no painel ao lado
            </div>
        );
    }

    return (
        <div className={`w-full ${bg ? 'rounded-lg p-4' : ''}`} style={bg ? { backgroundColor: bg } : undefined}>
            {title && (
                <p className={`font-bold mb-2 ${textAlign}`} style={{ color, fontSize: Math.round(size * 1.15), lineHeight: 1.3 }}>
                    {title}
                </p>
            )}
            <ul className={`flex flex-col gap-1.5 ${listPos}`}>
                {items.map((item, index) => (
                    <li key={index} className="flex items-start gap-2" style={{ color, fontSize: size, lineHeight: 1.35 }}>
                        <Marker type={listType} index={index} size={size} color={iconColor} />
                        <span className="min-w-0 break-words text-left">{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

interface CheckoutListProps {
    selected: boolean;
    config: ListConfig;
    onSelect: () => void;
    onDelete?: () => void;
    onDuplicate?: () => void;
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
    viewMode?: boolean;
}

const CheckoutList: React.FC<CheckoutListProps> = ({
    selected,
    config,
    onSelect,
    onDelete,
    onDuplicate,
    dragHandleProps,
    viewMode = false,
}) => {
    if (viewMode) {
        return <ListBlock config={config} />;
    }

    return (
        <div
            className={`relative group w-full cursor-pointer rounded-lg ${selected ? 'ring-2 ring-blue-500' : 'hover:ring-2 hover:ring-blue-300'}`}
            onClick={onSelect}
        >
            <ListBlock config={config} placeholderWhenEmpty />

            {selected && (
                <div className="absolute top-2 right-2 flex gap-1 z-10">
                    <span
                        {...dragHandleProps}
                        className="p-1.5 rounded bg-white shadow-lg border text-gray-600 hover:text-gray-800 cursor-grab active:cursor-grabbing"
                        title="Arrastar"
                    >
                        <i className="ri-drag-move-2-line text-sm"></i>
                    </span>
                    {onDuplicate && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
                            className="p-1.5 rounded bg-white shadow-lg border text-gray-600 hover:text-gray-800"
                            title="Duplicar"
                        >
                            <i className="ri-file-copy-line text-sm"></i>
                        </button>
                    )}
                    {onDelete && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDelete(); }}
                            className="p-1.5 rounded bg-white shadow-lg border text-red-600 hover:text-red-800"
                            title="Excluir"
                        >
                            <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default CheckoutList;
